/**
 * @fileoverview Workflow editor route (MOR-696, renamed MOR-716)
 *
 * URL: /dashboard/workflows/$workflowId
 *
 * Full-page editor for a single reusable workflow (settings + task
 * graph). Admin/owner can edit; members get a read-only view. Reached from the
 * workflows manager (create / edit / duplicate / AI import).
 *
 * - Convex: tasks.workflows.getTemplate / update / replaceItems
 */
import { createFileRoute } from "@tanstack/react-router";
import type { Id } from "@cvx/_generated/dataModel";
import { useActiveOrg } from "@/utils/org-context";
import { WorkflowEditor } from "@/components/settings/tasks/WorkflowEditor";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/workflows/$workflowId",
)({
  component: WorkflowEditorPage,
  // `?tab=automations` deep-links straight to the workflow's automations
  // (used by the global Automations page's "Edit in workflow" action).
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === "automations" ? ("automations" as const) : undefined,
  }),
});

function WorkflowEditorPage() {
  const { workflowId } = Route.useParams();
  const { tab } = Route.useSearch();
  const { orgId, activeOrg } = useActiveOrg();
  const canEdit = activeOrg?.role === "owner" || activeOrg?.role === "admin";

  return (
    <div className="flex flex-1 flex-col bg-muted p-6">
      {orgId ? (
        <WorkflowEditor
          orgId={orgId as Id<"organizations">}
          templateId={workflowId as Id<"taskTemplates">}
          canEdit={canEdit}
          initialTab={tab}
        />
      ) : (
        <div className="text-sm text-muted-foreground">
          Select an organisation to edit workflows.
        </div>
      )}
    </div>
  );
}
